import { PRESET_MONSTERS } from '../../data/presets';
import { chapterStates, stageUnlocked, totalStars } from '../../systems/progression';
import { isBossFloor, nextTowerFloor, towerMonster, towerMonsterId } from '../../systems/tower';
import { DAILY_ROLLS } from '../../systems/board';
import { openDiceBoard } from '../../../ui/diceBoard';
import { button, thumb } from '../../../ui/components';
import { h } from '../../../ui/dom';
import { icon } from '../../../ui/icons';
import { t } from '../../../i18n';
import type { NotebookCtx } from './tabs';

/** Story chapters, the tower and the dice board for the Quests page, plus shared fight helpers. */

/** ★★☆ rating (aria-labelled). */
export function starRow(n: number, max = 3): HTMLElement {
  return h(
    'span',
    { class: 'star-row', role: 'img', 'aria-label': t('{n} of {max} stars', { n, max }) },
    Array.from({ length: max }, (_, i) => h('span', { class: i < n ? 'on' : 'off', 'aria-hidden': 'true' }, i < n ? '★' : '☆')),
  );
}

/** Start a single-player fight, or send the player to make a hero first. */
export function startFight(ctx: NotebookCtx, monsterId: string): void {
  const { app } = ctx;
  if (!app.store.hero) {
    app.toast(t('Draw a hero first — monsters need someone to fight!'), 'info');
    ctx.goTab('newhero');
    return;
  }
  void app.scenes.go('battle', { mode: 'pve', monsterId }, 'page');
}

/** Story chapters with their stages, for the Quest Pages tab. */
export function chapterPages(ctx: NotebookCtx): HTMLElement {
  const { app } = ctx;
  const save = app.store.state;
  return h(
    'ol',
    { class: 'chapter-list' },
    chapterStates(save).map((c) =>
      h(
        'li',
        { class: `chapter-page ${c.unlocked ? '' : 'locked'} ${c.starLocked ? 'star-locked' : ''} ${c.complete ? 'complete' : ''}` },
        h(
          'header',
          { class: 'chapter-head' },
          h('span', { class: 'chapter-no' }, String(c.index + 1)),
          h(
            'div',
            null,
            h('b', null, c.unlocked || c.starLocked ? t(c.def.title) : '????'),
            h('small', null, c.unlocked ? t(c.def.subtitle) : c.starLocked ? t('Collect {n} stars to open this chapter', { n: c.starsNeeded }) : t('Finish the previous chapter to unlock')),
          ),
          h('span', { class: 'chapter-progress' }, c.complete ? t('Complete!') : c.starLocked ? `★ ${totalStars(save)} / ${c.starsNeeded}` : `${c.beaten} / ${c.def.stages.length}`),
        ),
        c.starLocked ? h('p', { class: 'chapter-story star-hint' }, t('Replay earlier stages and win with more HP left for more stars (★★ at half HP, ★★★ at 80%).')) : null,
        c.unlocked ? h('p', { class: 'chapter-story' }, t(c.def.story)) : null,
        c.unlocked
          ? h(
              'ul',
              { class: 'stage-list' },
              c.def.stages.map((id, i) => {
                const p = PRESET_MONSTERS.find((x) => x.id === id)!;
                const open = stageUnlocked(save, id);
                const beaten = save.defeatedPresets.includes(id);
                const boss = i === c.def.stages.length - 1;
                return h(
                  'li',
                  { class: `stage ${open ? '' : 'locked'} ${beaten ? 'beaten' : ''} ${boss ? 'boss' : ''}` },
                  open ? thumb(app, null, `monster:${id}`, 48) : h('span', { class: 'qmark' }, '?'),
                  h('div', { class: 'stage-name' }, h('b', null, open ? t(p.name) : '???'), h('small', null, boss ? t('Boss') : `Lv. ${p.level}`)),
                  beaten ? starRow(save.monsterStars[id] ?? 1) : null,
                  open
                    ? button(beaten ? t('Replay') : t('Fight!'), { small: true, variant: beaten ? 'ghost' : 'primary', color: 'pink', sfx: app, onClick: () => startFight(ctx, `preset:${id}`) })
                    : null,
                );
              }),
            )
          : null,
        h('p', { class: 'chapter-reward' }, t('Chapter reward: {coins} coins', { coins: c.def.reward.coins }), save.chaptersDone.includes(c.def.id) ? ` ✓` : ''),
      ),
    ),
  );
}

/** Homework Tower: one card with the next floor, the best floor and a climb button. */
export function towerSection(ctx: NotebookCtx): HTMLElement {
  const { app } = ctx;
  const save = app.store.state;
  const floor = nextTowerFloor(save);
  const m = towerMonster(floor);
  const boss = isBossFloor(floor);
  return h(
    'section',
    { class: `tower-card ${boss ? 'boss' : ''}` },
    h('header', { class: 'tower-head' }, h('h3', { class: 'tape-title' }, icon('trophy'), h('span', null, t('Homework Tower'))), h('span', { class: 'tower-best' }, t('Best: floor {n}', { n: save.counters.towerBest ?? 0 }))),
    h('p', { class: 'hand-hint' }, t('An endless climb. Every floor is a little tougher, every fifth floor is a boss.')),
    h(
      'div',
      { class: 'tower-next' },
      thumb(app, null, `monster:${m.preset}`, 64),
      h(
        'div',
        { class: 'tower-next-text' },
        h('b', null, t('Floor {n}', { n: floor }), boss ? ` · ${t('Boss')}` : ''),
        h('small', null, `${t(m.name)} · HP ${m.hp} · ATK ${m.atk} · DEF ${m.def}`),
        h('small', null, `+${m.reward.exp} EXP · +${m.reward.coins} ${t('coins')}`),
      ),
      button(t('Climb!'), { small: true, variant: 'primary', color: 'pink', sfx: app, onClick: () => startFight(ctx, towerMonsterId(floor)) }),
    ),
  );
}

/** Dice board: rolls left and a button that opens the board. */
export function diceBoardSection(ctx: NotebookCtx): HTMLElement {
  const { app } = ctx;
  const b = app.store.state.board;
  return h(
    'section',
    { class: 'tower-card dice-card' },
    h('header', { class: 'tower-head' }, h('h3', { class: 'tape-title' }, '🎲 ', h('span', null, t('Dice board'))), h('span', { class: 'tower-best' }, t('Laps: {n}', { n: app.store.state.counters.boardLaps }))),
    h(
      'div',
      { class: 'tower-next' },
      h('div', { class: 'tower-next-text' }, h('b', null, t('Rolls left: {n}/{max}', { n: b.rolls, max: 10 })), h('small', null, t('{n} free rolls every day, plus one for every battle you win.', { n: DAILY_ROLLS }))),
      button(t('Play'), { small: true, variant: 'primary', color: 'green', sfx: app, onClick: () => void openDiceBoard(app).then(() => ctx.goTab('quests')) }),
    ),
  );
}
